import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { pacientesService } from '../api/pacientes';
import { insumosService } from '../api/insumos';
import { citasService } from '../api/citas';
import { differenceInYears, parseISO, format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { 
  MagnifyingGlassIcon, 
  PencilIcon, 
  PrinterIcon, 
  XMarkIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  HeartIcon,
  ScaleIcon
} from '@heroicons/react/24/outline';
import InteractivePodogram from '../components/ficha/InteractivePodogram';
import PrescriptionModal from '../components/ficha/PrescriptionModal';

const FichaClinicaPage = () => {
  const { currentUser } = useAuth();
  const { canAccessInventory } = usePermissions();

  // Determinar especialidad de la cuenta para campos adaptables
  const especialidad = currentUser?.cuenta?.especialidad || 'podologia';
  const esPodologia = especialidad === 'podologia' || (especialidad === 'multi');
  const esCosmetologia = especialidad === 'cosmetologia' || (especialidad === 'multi');
  const esManicuria = especialidad === 'manicuria' || (especialidad === 'multi');
  const esMulti = especialidad === 'multi';
  const [pacientes, setPacientes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPaciente, setSelectedPaciente] = useState(null);
  const [fichas, setFichas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedFicha, setSelectedFicha] = useState(null);
  const [showCertificado, setShowCertificado] = useState(false);
  const [showPrescription, setShowPrescription] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [viewMode, setViewMode] = useState('none'); // 'none', 'view', 'edit'

  // FC-003: Fotos de antes/después
  const [fotos, setFotos] = useState([]);
  const [uploadTipo, setUploadTipo] = useState('antes');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loadingFotos, setLoadingFotos] = useState(false);
  const [activePhoto, setActivePhoto] = useState(null);

  const mousedownOnPhotoBackdrop = useRef(false);
  const handlePhotoBackdropMouseDown = (e) => {
    if (e.target === e.currentTarget) {
      mousedownOnPhotoBackdrop.current = true;
    } else {
      mousedownOnPhotoBackdrop.current = false;
    }
  };
  const handlePhotoBackdropMouseUp = (e) => {
    if (e.target === e.currentTarget && mousedownOnPhotoBackdrop.current) {
      setActivePhoto(null);
    }
    mousedownOnPhotoBackdrop.current = false;
  };

  const handlePrevPhoto = useCallback(() => {
    if (!activePhoto || fotos.length <= 1) return;
    const index = fotos.findIndex(f => f.id === activePhoto.id);
    const prevIndex = (index - 1 + fotos.length) % fotos.length;
    setActivePhoto(fotos[prevIndex]);
  }, [activePhoto, fotos]);

  const handleNextPhoto = useCallback(() => {
    if (!activePhoto || fotos.length <= 1) return;
    const index = fotos.findIndex(f => f.id === activePhoto.id);
    const nextIndex = (index + 1) % fotos.length;
    setActivePhoto(fotos[nextIndex]);
  }, [activePhoto, fotos]);

  useEffect(() => {
    if (!activePhoto) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        handlePrevPhoto();
      } else if (e.key === 'ArrowRight') {
        handleNextPhoto();
      } else if (e.key === 'Escape') {
        setActivePhoto(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activePhoto, handlePrevPhoto, handleNextPhoto]);

  const [formData, setFormData] = useState({
    fecha: new Date().toISOString().split('T')[0],
    descripcion_atencion: '',
    procedimiento: '',
    indicaciones: '',
    productos_usados: [],
    cita_id: null,
    // Nuevos campos clínicos
    anamnesis: {
      hta: false,
      htaControlada: false,
      dm: false,
      dmTipo: '',
      dmAnios: '',
      alergias: '',
      farmacos: '',
      otros: ''
    },
    examenFisico: {
      peso: '',
      talla: '',
      imc: '',
      presionArterial: '',
      pulsoRadial: '',
      pulsoPedioIzq: 'presente',
      pulsoPedioDer: 'presente',
      sensibilidad: 'conservada',
      tipoPie: 'normal',
      calzado: 'adecuado'
    },
    podograma: [], // Array de { x, y, tipo, nota, timestamp }
    // Campos cosmetológicos
    tipo_piel: '',
    fototipo: '',
    zona_tratada: '',
    consentimiento_firmado: false,
    alergias_cosmeticas: '',
    productos_aplicados: '',
    proxima_sesion_estimada: ''
  });

  // Estado para control de tabs en el modal
  const [activeTab, setActiveTab] = useState(0);

  // Helper para resetear formData
  const getInitialFormData = () => ({
    fecha: new Date().toISOString().split('T')[0],
    descripcion_atencion: '',
    procedimiento: '',
    indicaciones: '',
    productos_usados: [],
    cita_id: null,
    anamnesis: {
      hta: false,
      htaControlada: false,
      dm: false,
      dmTipo: '',
      dmAnios: '',
      alergias: '',
      farmacos: '',
      otros: ''
    },
    examenFisico: {
      peso: '',
      talla: '',
      imc: '',
      presionArterial: '',
      pulsoRadial: '',
      pulsoPedioIzq: 'presente',
      pulsoPedioDer: 'presente',
      sensibilidad: 'conservada',
      tipoPie: 'normal',
      calzado: 'adecuado'
    },
    podograma: [],
    // Campos cosmetológicos
    tipo_piel: '',
    fototipo: '',
    zona_tratada: '',
    consentimiento_firmado: false,
    alergias_cosmeticas: '',
    productos_aplicados: '',
    proxima_sesion_estimada: ''
  });

  const [certificadoData, setCertificadoData] = useState({
    fecha_emision: new Date().toISOString().split('T')[0],
    motivo: '',
    indicacion: '',
    dias_reposo: '',
    tipo_certificado: 'general' // general, deportivo, laboral
  });

  const [productosInventario, setProductosInventario] = useState([]);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [cantidadProducto, setCantidadProducto] = useState(1);
  
  // Para pre-carga desde cita completada
  const [searchParams, setSearchParams] = useSearchParams();
  const [citaOrigen, setCitaOrigen] = useState(null);
  const [recetaCargada, setRecetaCargada] = useState(false);

  useEffect(() => {
    cargarPacientes();
    cargarProductosInventario();
  }, []);

  // Cálculo automático de IMC
  useEffect(() => {
    // Guard clause: only calculate if examenFisico exists
    if (!formData.examenFisico) return;
    
    const peso = parseFloat(formData.examenFisico.peso);
    const tallaCm = parseFloat(formData.examenFisico.talla);
    if (peso && tallaCm && tallaCm > 0) {
      const tallaM = tallaCm / 100;
      const imc = (peso / (tallaM * tallaM)).toFixed(1);
      setFormData(prev => ({
        ...prev,
        examenFisico: { ...prev.examenFisico, imc }
      }));
    }
  }, [formData.examenFisico?.peso, formData.examenFisico?.talla]);

  // FC-003: Cargar fotos cuando se selecciona una ficha
  useEffect(() => {
    if (selectedFicha?.id) {
      cargarFotos(selectedFicha.id);
    } else {
      setFotos([]);
    }
  }, [selectedFicha?.id]);

  const cargarPacientes = async () => {
    try {
      const response = await pacientesService.getAll();
      setPacientes(response.data);
    } catch (error) {
      console.error('Error al cargar pacientes:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarProductosInventario = async () => {
    try {
      const response = await insumosService.getAll();
      setProductosInventario(response.data);
    } catch (error) {
      console.error('Error al cargar productos de inventario:', error);
    }
  };

  // FC-003: Cargar fotos de una ficha
  const cargarFotos = async (fichaId) => {
    setLoadingFotos(true);
    try {
      const response = await pacientesService.getFotos(fichaId);
      setFotos(response.data);
    } catch (error) {
      console.error('Error al cargar fotos:', error);
    } finally {
      setLoadingFotos(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadFile(file);
    setUploadPreview(URL.createObjectURL(file));
  };

  const handleUploadFoto = async () => {
    if (!uploadFile || !selectedFicha) return;
    setUploading(true);
    try {
      await pacientesService.uploadFoto(selectedFicha.id, uploadTipo, uploadFile);
      setUploadFile(null);
      setUploadPreview(null);
      await cargarFotos(selectedFicha.id);
      toast.success('Foto subida correctamente');
    } catch (error) {
      console.error('Error al subir foto:', error);
      toast.error('Error al subir la foto');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFoto = async (fotoId) => {
    if (!window.confirm('¿Eliminar esta foto?')) return;
    try {
      await pacientesService.deleteFoto(fotoId);
      setFotos(prev => prev.filter(f => f.id !== fotoId));
      toast.success('Foto eliminada');
    } catch (error) {
      console.error('Error al eliminar foto:', error);
      toast.error('Error al eliminar la foto');
    }
  };

  // Efecto para manejar redirección desde cita completada
  useEffect(() => {
    const nuevo = searchParams.get('nuevo');
    const citaId = searchParams.get('cita_id');
    const pacienteRut = searchParams.get('paciente_rut');
    const tratamientoId = searchParams.get('tratamiento_id');
    
    if (nuevo === 'true' && pacienteRut && !recetaCargada) {
      
      // Guardar info de cita origen
      setCitaOrigen({ citaId, pacienteRut, tratamientoId });
      
      // Función para cargar todo
      const cargarDatosCita = async () => {
        try {
          // 1. Buscar y seleccionar el paciente
          const pacientesResponse = await pacientesService.getAll();
          const paciente = pacientesResponse.data.find(p => p.rut === pacienteRut);
          
          if (paciente) {
            // Seleccionar paciente
            setSelectedPaciente(paciente);
            const fichasResponse = await pacientesService.getFichaClinica(pacienteRut);
            const fichasOrdenadas = fichasResponse.data.sort((a, b) => 
              new Date(b.fecha) - new Date(a.fecha)
            );
            setFichas(fichasOrdenadas);
            
            // Abrir formulario de nueva ficha
            setShowForm(true);
            
            // 2. Cargar receta si hay tratamiento_id
            if (tratamientoId) {
              try {
                const recetaResponse = await citasService.getRecetaTratamiento(tratamientoId);
                
                if (recetaResponse.data.insumos && recetaResponse.data.insumos.length > 0) {
                  // Pre-cargar productos de la receta
                  const productosPreCargados = recetaResponse.data.insumos.map(item => ({
                    id: item.insumo_id,
                    nombre: item.nombre,
                    cantidad: item.cantidad,
                    insumo: item.insumo_id
                  }));
                  
                  setFormData(prev => ({
                    ...prev,
                    productos_usados: productosPreCargados
                  }));
                  
                  // Mostrar toast de éxito
                  const toast = document.createElement('div');
                  toast.className = 'fixed bottom-4 right-4 bg-[#2A9D8F] text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-pulse';
                  toast.innerHTML = `✨ Receta sugerida cargada para <strong>${recetaResponse.data.tratamiento_nombre}</strong>. Confirma el uso real.`;
                  document.body.appendChild(toast);
                  setTimeout(() => toast.remove(), 5000);
                }
              } catch (error) {
                // No hay receta configurada para este tratamiento
              }
            }
            
            // Limpiar query params
            setSearchParams({});
            setRecetaCargada(true);
          }
        } catch (error) {
          console.error('Error al cargar datos de cita:', error);
        }
      };
      
      // Ejecutar después de que se carguen los pacientes
      if (pacientes.length > 0) {
        cargarDatosCita();
      }
    }
  }, [searchParams, pacientes, recetaCargada, setSearchParams]);

  const handlePacienteSelect = async (rut) => {
    try {
      const response = await pacientesService.getFichaClinica(rut);
      // Ordenar fichas por fecha de más reciente a más antigua
      const fichasOrdenadas = response.data.sort((a, b) => 
        new Date(b.fecha) - new Date(a.fecha)
      );
      setFichas(fichasOrdenadas);
      setSelectedPaciente(pacientes.find(p => p.rut === rut));
    } catch (error) {
      console.error('Error al cargar fichas:', error);
    }
  };

  const handleFichaClick = (ficha) => {
    if (viewMode !== 'none') return; // No hacer nada si ya hay un modal abierto
    setSelectedFicha(ficha);
    setViewMode('view');
  };

  const handleEditClick = (e, ficha) => {
    e.stopPropagation(); // Evitar que se propague al click de la ficha
    setSelectedFicha(ficha);
    
    // Asegurarse de que los productos usados estén correctamente formateados
    const productosFormateados = ficha.productos_usados.map(p => ({
      id: p.insumo,
      nombre: p.nombre_insumo || p.nombre,
      cantidad: p.cantidad,
      insumo: p.insumo // Asegurar que tengamos ambos campos
    }));
    
    // Combinar datos existentes con la estructura inicial para asegurar que los nuevos campos existan
    const initialData = getInitialFormData();
    
    setFormData({
      ...initialData, // Base con todos los campos (anamnesis, examenFisico, etc)
      // Sobrescribir con datos de la ficha existente
      fecha: ficha.fecha,
      descripcion_atencion: ficha.descripcion_atencion,
      procedimiento: ficha.procedimiento,
      indicaciones: ficha.indicaciones,
      productos_usados: productosFormateados,
      // Si la ficha ya tiene estos campos guardados, usarlos (si no, quedan los del initialData)
      ...(ficha.anamnesis && { anamnesis: ficha.anamnesis }),
      ...(ficha.examenFisico && { examenFisico: ficha.examenFisico }),
      ...(ficha.podograma && { podograma: ficha.podograma }),
      // Campos cosmetológicos (cargar si existen)
      tipo_piel: ficha.tipo_piel || initialData.tipo_piel,
      fototipo: ficha.fototipo || initialData.fototipo,
      zona_tratada: ficha.zona_tratada || initialData.zona_tratada,
      consentimiento_firmado: ficha.consentimiento_firmado ?? initialData.consentimiento_firmado,
      alergias_cosmeticas: ficha.alergias_cosmeticas || initialData.alergias_cosmeticas,
      productos_aplicados: ficha.productos_aplicados || initialData.productos_aplicados,
      proxima_sesion_estimada: ficha.proxima_sesion_estimada || initialData.proxima_sesion_estimada
    });
    setViewMode('edit');
  };

  const handleCloseModal = () => {
    if (viewMode === 'edit') {
      setViewMode('view');
    } else {
      setSelectedFicha(null);
      setViewMode('none');
    }
    
    setFormData(getInitialFormData());
    setActiveTab(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPaciente) return;

    try {
      // Preparar los datos de la ficha
      // Para usuarios sin acceso a inventario (plan básico), enviar array vacío
      const productosFormateados = canAccessInventory 
        ? formData.productos_usados.map(p => ({
            insumo: parseInt(p.id || p.insumo),
            cantidad: parseInt(p.cantidad),
            nombre_insumo: p.nombre
          }))
        : [];
      
      // DETECCIÓN INTELIGENTE DEL CITA_ID
      // Prioridad: 1. Estado de navegación, 2. URL params, 3. Estado del formulario (botón)
      const citaIdParaEnviar = citaOrigen?.citaId 
                            || searchParams.get('cita_id') 
                            || formData.cita_id;
      
      const fichaData = {
        fecha: formData.fecha,
        descripcion_atencion: formData.descripcion_atencion,
        procedimiento: formData.procedimiento,
        indicaciones: formData.indicaciones,
        productos_usados_data: productosFormateados,
        paciente: selectedPaciente.id,
        // Nuevos campos clínicos (JSON)
        anamnesis: formData.anamnesis,
        examenFisico: formData.examenFisico,
        podograma: formData.podograma,
        // Campos cosmetológicos
        tipo_piel: formData.tipo_piel || null,
        fototipo: formData.fototipo || null,
        zona_tratada: formData.zona_tratada || null,
        consentimiento_firmado: formData.consentimiento_firmado || false,
        alergias_cosmeticas: formData.alergias_cosmeticas || null,
        productos_aplicados: formData.productos_aplicados || null,
        proxima_sesion_estimada: formData.proxima_sesion_estimada || null,
        // Solo agregar 'cita' si tenemos un ID válido
        ...(citaIdParaEnviar && { cita: parseInt(citaIdParaEnviar) })
      };

      let response;
      if (viewMode === 'edit' && selectedFicha) {
        response = await pacientesService.updateFichaClinica(selectedFicha.id, fichaData);
        if (response.status === 200) {
          // Actualizar la ficha seleccionada con los datos de la respuesta
          const fichaActualizada = response.data;
          setSelectedFicha(fichaActualizada);
          
          // Actualizar la lista de fichas
          setFichas(prevFichas => 
            prevFichas.map(ficha => 
              ficha.id === selectedFicha.id ? fichaActualizada : ficha
            )
          );
          
          await cargarProductosInventario(); // Recargar inventario
          setViewMode('view');
          
          // Mostrar mensaje de éxito
          toast.success('Ficha clínica actualizada correctamente');
        }
      } else {
        response = await pacientesService.createFichaClinica(selectedPaciente.rut, fichaData);
        if (response.status === 201) {
          await handlePacienteSelect(selectedPaciente.rut);
          await cargarProductosInventario(); // Recargar inventario
          
          // Recargar lista de pacientes y actualizar selectedPaciente con datos frescos
          const pacientesResponse = await pacientesService.getAll();
          setPacientes(pacientesResponse.data);
          const pacienteActualizado = pacientesResponse.data.find(p => p.rut === selectedPaciente.rut);
          if (pacienteActualizado) {
            setSelectedPaciente(pacienteActualizado);
          }
          
          setShowForm(false);
          
          // Mostrar mensaje de éxito
          toast.success('Ficha clínica creada correctamente');
        }
      }
      
      // Resetear formulario
      setFormData(getInitialFormData());
      setActiveTab(0);
    } catch (error) {
      console.error('Error al guardar ficha:', error);
      if (error.response) {
        console.error('Detalles del error:', error.response.data);
        
        // Mostrar mensaje de error
        if (error.response.data && typeof error.response.data === 'object') {
          const errorMessages = [];
          for (const key in error.response.data) {
            errorMessages.push(`${key}: ${error.response.data[key]}`);
          }
          toast.error(`Error: ${errorMessages.join(', ')}`);
        } else {
          toast.error(`Error: ${error.message}`);
        }
      } else {
        toast.error(`Error: ${error.message}`);
      }
    }
  };

  const limpiarRut = (rut) => {
    return (rut || '').replace(/[^0-9kK]/g, '');
  };

  const filteredPacientes = pacientes.filter(paciente => {
    const searchTermLower = searchTerm.toLowerCase().trim();
    
    // Si el término de búsqueda está vacío, mostrar todos
    if (!searchTermLower) return true;
    
    // Filtrar por nombre (siempre buscar en el nombre)
    const nombreMatch = paciente.nombre.toLowerCase().includes(searchTermLower);
    
    // Filtrar por RUT solo si el término de búsqueda contiene números o 'k'
    let rutMatch = false;
    if (/[0-9kK]/.test(searchTerm)) {
      const searchTermLimpio = limpiarRut(searchTerm);
      const rutPacienteLimpio = limpiarRut(paciente.rut);
      rutMatch = rutPacienteLimpio.includes(searchTermLimpio);
    }
    
    return nombreMatch || rutMatch;
  })
  // Ordenar: pacientes con ficha_pendiente primero
  .sort((a, b) => {
    if (a.ficha_pendiente && !b.ficha_pendiente) return -1;
    if (!a.ficha_pendiente && b.ficha_pendiente) return 1;
    return a.nombre.localeCompare(b.nombre);
  });

  const formatDate = (dateString) => {
    try {
      // Usar parseISO para interpretar correctamente la fecha sin problemas de timezone
      const date = parseISO(dateString);
      return format(date, "d 'de' MMMM 'de' yyyy", { locale: es });
    } catch (error) {
      console.error('Error al formatear fecha:', error, dateString);
      return dateString; // Devolver la fecha original si hay error
    }
  };

  const handleGenerarCertificado = () => {
    if (!selectedFicha) return;
    
    setCertificadoData({
      fecha_emision: new Date().toISOString().split('T')[0],
      motivo: selectedFicha.descripcion_atencion,
      indicacion: selectedFicha.indicaciones,
      dias_reposo: '',
      tipo_certificado: 'general'
    });
    setShowCertificado(true);
  };

  const handlePrintCertificado = () => {
    window.print();
  };

  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return null;
    try {
      // Usar parseISO para interpretar correctamente la fecha sin problemas de timezone
      const fechaNac = parseISO(fechaNacimiento);
      const edad = differenceInYears(new Date(), fechaNac);
      return edad;
    } catch (error) {
      console.error('Error al calcular edad:', error, fechaNacimiento);
      return null;
    }
  };

  const agregarProducto = () => {
    if (!productoSeleccionado || cantidadProducto < 1) return;
    
    // Verificar que no se exceda el stock disponible
    if (cantidadProducto > productoSeleccionado.stock_actual) {
      toast.error(`No hay suficiente stock disponible. Stock actual: ${productoSeleccionado.stock_actual}`);
      return;
    }
    
    // Clonar el producto seleccionado para evitar referencias cruzadas
    const nuevoProducto = {
      id: productoSeleccionado.id,
      nombre: productoSeleccionado.nombre,
      cantidad: cantidadProducto,
      insumo: productoSeleccionado.id
    };
    
    // Actualizar el estado de productos usados de forma inmutada
    const nuevosProductos = [...formData.productos_usados, nuevoProducto];
    
    // Actualizar el estado del formulario con los nuevos productos
    setFormData(prevState => ({
      ...prevState,
      productos_usados: nuevosProductos
    }));
    
    // Limpiar la selección actual
    setProductoSeleccionado(null);
    setCantidadProducto(1);
  };

  const eliminarProducto = (index) => {
    setFormData(prevState => ({
      ...prevState,
      productos_usados: prevState.productos_usados.filter((_, i) => i !== index)
    }));
    
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Cargando...</div>;
  }

  return (
    <div className="p-3 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 space-y-2 sm:space-y-0">
        <h1 className="text-xl sm:text-2xl font-bold">Fichas Clínicas</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Lista de Pacientes - Panel Izquierdo */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Pacientes</h2>
            {/* Buscador moderno con icono */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" strokeWidth={1.5} />
              <input
                type="text"
                placeholder="Buscar por nombre o RUT..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-full border border-gray-200 bg-gray-50 
                  focus:outline-none focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] 
                  text-base transition-all duration-200"
              />
            </div>
          </div>
          <div className="max-h-[40vh] sm:max-h-[calc(100vh-300px)] overflow-y-auto">
            {filteredPacientes.map(paciente => (
              <button
                key={paciente.id}
                onClick={() => handlePacienteSelect(paciente.rut)}
                className={`w-full text-left p-4 border-b border-gray-50 transition-all duration-200 flex items-center gap-3 ${
                  selectedPaciente?.rut === paciente.rut
                    ? 'border-l-4 border-l-[#E76F51] bg-[#F4E1D2]/50'
                    : 'border-l-4 border-l-transparent hover:bg-[#F4E1D2]/30'
                }`}
              >
                {/* Avatar con iniciales */}
                <div className="flex-shrink-0 w-10 h-10 bg-[#2A9D8F] text-white rounded-full flex items-center justify-center font-semibold text-sm">
                  {paciente.nombre.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-base font-medium text-gray-900 truncate">{paciente.nombre}</p>
                    {paciente.ficha_pendiente && (
                      <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        ⚠️ Ficha
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{paciente.rut}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Panel Derecho - Detalle del Paciente */}
        <div className="lg:col-span-2">
          {selectedPaciente ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Hero Header con Gradiente */}
              <div className="bg-gradient-to-r from-[#2A9D8F]/10 via-[#2A9D8F]/5 to-transparent p-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                  <div className="flex items-start gap-4">
                    {/* Avatar grande */}
                    <div className="flex-shrink-0 w-16 h-16 bg-[#2A9D8F] text-white rounded-full flex items-center justify-center font-bold text-xl">
                      {selectedPaciente.nombre.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()}
                    </div>
                    <div>
                      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{selectedPaciente.nombre}</h2>
                      <div className="flex flex-wrap items-center gap-2 mt-1 text-[#2A9D8F]">
                        {selectedPaciente.fecha_nacimiento && (
                          <span className="font-semibold">{calcularEdad(selectedPaciente.fecha_nacimiento)} años</span>
                        )}
                        <span className="text-gray-400">•</span>
                        <span>{selectedPaciente.rut}</span>
                      </div>
                      {selectedPaciente.direccion && (
                        <p className="text-sm text-gray-500 mt-1">{selectedPaciente.direccion}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      // Si hay ficha pendiente, pre-llenar con datos de la cita
                      // Si hay ficha pendiente, pre-llenar con datos de la cita
                      if (selectedPaciente.ficha_pendiente && selectedPaciente.citas_pendientes?.length > 0) {
                        const citaPendiente = selectedPaciente.citas_pendientes[0];
                        const initialData = getInitialFormData();
                        
                        setFormData({
                          ...initialData,
                          fecha: citaPendiente.fecha,
                          descripcion_atencion: `Atención: ${citaPendiente.tratamiento}`,
                          // Mantener el resto de la estructura inicial
                          cita_id: citaPendiente.id
                        });
                      }
                      setShowForm(true);
                    }}
                    className={`flex-shrink-0 px-6 py-3 rounded-lg shadow-lg 
                      transition-all duration-200 font-medium flex items-center gap-2 ${
                      selectedPaciente.ficha_pendiente
                        ? 'bg-amber-500 hover:bg-amber-600 text-white animate-pulse'
                        : 'bg-[#E76F51] hover:bg-[#d65f41] text-white'
                    }`}
                  >
                    <DocumentTextIcon className="w-5 h-5" strokeWidth={1.5} />
                    {selectedPaciente.ficha_pendiente ? '⚡ Crear Ficha Pendiente' : 'Nueva Ficha'}
                  </button>
                </div>
              </div>

              {/* Lista de Fichas */}
              <div className="p-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Historial de Fichas ({fichas.length})
                </h3>
                <div className="space-y-3 max-h-[40vh] sm:max-h-[calc(100vh-380px)] overflow-y-auto pr-2">
                  {fichas.length > 0 ? (
                    fichas.map((ficha, index) => (
                      <button
                        key={index}
                        onClick={() => handleFichaClick(ficha)}
                        className={`w-full text-left p-4 border rounded-lg transition-all duration-200 group ${
                          selectedFicha?.id === ficha.id
                            ? 'bg-[#F4E1D2]/50 border-[#E76F51]/30 shadow-sm'
                            : 'border-gray-100 hover:border-[#2A9D8F]/30 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-gray-900">{formatDate(ficha.fecha)}</h3>
                          <ClipboardDocumentListIcon className="w-5 h-5 text-gray-400 group-hover:text-[#2A9D8F] transition-colors" strokeWidth={1.5} />
                        </div>
                        <p className="text-gray-600 text-sm line-clamp-2">{ficha.descripcion_atencion}</p>
                      </button>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <ClipboardDocumentListIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" strokeWidth={1} />
                      <p>No hay fichas registradas</p>
                      <p className="text-sm mt-1">Crea la primera ficha clínica para este paciente</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* Empty State con Ilustración SVG */
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col items-center justify-center py-16 px-8">
              <svg className="w-48 h-48 text-gray-200 mb-6" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="40" y="30" width="120" height="150" rx="8" fill="currentColor" opacity="0.3"/>
                <rect x="50" y="50" width="80" height="8" rx="2" fill="currentColor" opacity="0.5"/>
                <rect x="50" y="70" width="100" height="6" rx="2" fill="currentColor" opacity="0.4"/>
                <rect x="50" y="85" width="90" height="6" rx="2" fill="currentColor" opacity="0.4"/>
                <rect x="50" y="100" width="95" height="6" rx="2" fill="currentColor" opacity="0.4"/>
                <rect x="50" y="120" width="60" height="8" rx="2" fill="currentColor" opacity="0.5"/>
                <rect x="50" y="140" width="85" height="6" rx="2" fill="currentColor" opacity="0.4"/>
                <rect x="50" y="155" width="70" height="6" rx="2" fill="currentColor" opacity="0.4"/>
                <circle cx="150" cy="150" r="35" fill="#2A9D8F" opacity="0.15"/>
                <path d="M140 150L148 158L162 144" stroke="#2A9D8F" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Selecciona un paciente</h3>
              <p className="text-gray-500 text-center max-w-sm">
                Elige un paciente de la lista para ver su historial de fichas clínicas
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Nueva Ficha - Sistema de 3 Tabs */}
      {showForm && (
        <div 
          className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full z-50 p-2 sm:p-4"
          onClick={() => setShowForm(false)}
        >
          <div 
            className="relative top-4 sm:top-6 mx-auto p-5 sm:p-6 border-0 w-full max-w-full sm:max-w-6xl shadow-2xl rounded-xl bg-white"
            onClick={e => e.stopPropagation()}
          >
            {/* Header del Modal */}
            <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-100">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {isEditing ? 'Editar Ficha Clínica' : 'Nueva Ficha Clínica'}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedPaciente?.nombre} • {selectedPaciente?.rut}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowForm(false);
                  setIsEditing(false);
                  setFormData(getInitialFormData());
                  setActiveTab(0);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-6 h-6" strokeWidth={1.5} />
              </button>
            </div>

            {/* Fecha de atención (siempre visible) */}
            <div className="mb-4">
              <label className="block text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-2">
                Fecha de Atención
              </label>
              <input
                type="date"
                value={formData.fecha}
                onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                className="w-full sm:w-auto px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg 
                  focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] text-base transition-all"
                required
              />
            </div>

            {/* Sistema de Tabs - Dinámico según especialidad */}
            {esManicuria && !esMulti ? (
              /* Manicuría pura: sin tabs, vista ultra-simplificada */
              <div className="mb-6" />
            ) : (
            <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
              {(esMulti
                ? [
                    { id: 0, name: '🥼 Antecedentes', icon: HeartIcon },
                    { id: 1, name: '🥼 Examen Físico', icon: ScaleIcon },
                    { id: 2, name: '💆 Cosmetología', icon: HeartIcon },
                    { id: 3, name: '💅 Manicuría', icon: HeartIcon },
                    { id: 4, name: 'Evolución & Atención', icon: DocumentTextIcon }
                  ]
                : esPodologia
                ? [
                    { id: 0, name: 'Antecedentes', icon: HeartIcon },
                    { id: 1, name: 'Examen Físico', icon: ScaleIcon },
                    { id: 2, name: 'Evolución & Podograma', icon: DocumentTextIcon }
                  ]
                : [
                    { id: 0, name: '💆 Cosmetología', icon: HeartIcon },
                    { id: 1, name: 'Evolución & Atención', icon: DocumentTextIcon }
                  ]
              ).map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'text-[#2A9D8F] border-[#2A9D8F]'
                      : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-5 h-5" strokeWidth={1.5} />
                  {tab.name}
                </button>
              ))}
            </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* TAB 1: ANTECEDENTES (Podología) */}
              {esPodologia && activeTab === 0 && (
                <div className="space-y-6 animate-in fade-in duration-200">
                  {/* Antecedentes Mórbidos */}
                  <div>
                    <h4 className="text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-4">
                      Antecedentes Mórbidos
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* HTA */}
                      <div className={`p-4 rounded-lg border-2 transition-colors ${
                        formData.anamnesis.hta ? 'border-[#2A9D8F] bg-[#2A9D8F]/5' : 'border-gray-200 bg-gray-50'
                      }`}>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.anamnesis.hta}
                            onChange={(e) => setFormData({
                              ...formData,
                              anamnesis: { ...formData.anamnesis, hta: e.target.checked, htaControlada: false }
                            })}
                            className="w-5 h-5 rounded border-gray-300 text-[#2A9D8F] focus:ring-[#2A9D8F]"
                          />
                          <span className="font-semibold text-gray-900">HTA (Hipertensión)</span>
                        </label>
                        {formData.anamnesis.hta && (
                          <div className="mt-3 ml-8">
                            <label className="flex items-center gap-2 text-sm text-gray-600">
                              <input
                                type="checkbox"
                                checked={formData.anamnesis.htaControlada}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  anamnesis: { ...formData.anamnesis, htaControlada: e.target.checked }
                                })}
                                className="w-4 h-4 rounded border-gray-300 text-[#2A9D8F] focus:ring-[#2A9D8F]"
                              />
                              Controlada
                            </label>
                          </div>
                        )}
                      </div>

                      {/* Diabetes Mellitus - Con borde rojo cuando activo */}
                      <div className={`p-4 rounded-lg border-2 transition-colors ${
                        formData.anamnesis.dm ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-gray-50'
                      }`}>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.anamnesis.dm}
                            onChange={(e) => setFormData({
                              ...formData,
                              anamnesis: { 
                                ...formData.anamnesis, 
                                dm: e.target.checked,
                                dmTipo: '',
                                dmAnios: ''
                              }
                            })}
                            className="w-5 h-5 rounded border-gray-300 text-red-500 focus:ring-red-500"
                          />
                          <span className="font-semibold text-gray-900">DM (Diabetes) ⚠️</span>
                        </label>
                        {formData.anamnesis.dm && (
                          <div className="mt-3 ml-8 grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Tipo</label>
                              <select
                                value={formData.anamnesis.dmTipo}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  anamnesis: { ...formData.anamnesis, dmTipo: e.target.value }
                                })}
                                className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm 
                                  focus:ring-2 focus:ring-red-300 focus:border-red-300 bg-white"
                              >
                                <option value="">Seleccionar</option>
                                <option value="1">Tipo 1</option>
                                <option value="2">Tipo 2</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Años evolución</label>
                              <input
                                type="number"
                                value={formData.anamnesis.dmAnios}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  anamnesis: { ...formData.anamnesis, dmAnios: e.target.value }
                                })}
                                placeholder="Años"
                                min="0"
                                className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm 
                                  focus:ring-2 focus:ring-red-300 focus:border-red-300 bg-white"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Alergias */}
                  <div>
                    <label className="block text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-2">
                      Alergias
                    </label>
                    <textarea
                      value={formData.anamnesis.alergias}
                      onChange={(e) => setFormData({
                        ...formData,
                        anamnesis: { ...formData.anamnesis, alergias: e.target.value }
                      })}
                      rows="2"
                      placeholder="Medicamentos, materiales, otros..."
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                        focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] text-base resize-none"
                    />
                  </div>

                  {/* Tratamiento Farmacológico */}
                  <div>
                    <label className="block text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-2">
                      Tratamiento Farmacológico Actual
                    </label>
                    <textarea
                      value={formData.anamnesis.farmacos}
                      onChange={(e) => setFormData({
                        ...formData,
                        anamnesis: { ...formData.anamnesis, farmacos: e.target.value }
                      })}
                      rows="3"
                      placeholder="Medicamentos que toma actualmente..."
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                        focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] text-base resize-none"
                    />
                  </div>

                  {/* Otras Patologías */}
                  <div>
                    <label className="block text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-2">
                      Otras Patologías
                    </label>
                    <textarea
                      value={formData.anamnesis.otros}
                      onChange={(e) => setFormData({
                        ...formData,
                        anamnesis: { ...formData.anamnesis, otros: e.target.value }
                      })}
                      rows="2"
                      placeholder="Otras condiciones relevantes..."
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                        focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] text-base resize-none"
                    />
                  </div>
                </div>
              )}

              {/* TAB 2: EXAMEN FÍSICO (Podología) */}
              {esPodologia && activeTab === 1 && (
                <div className="space-y-6 animate-in fade-in duration-200">
                  <h4 className="text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-4">
                    Examen Físico General
                  </h4>

                  {/* Grid de 3 columnas */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Columna 1: General */}
                    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                      <h5 className="font-semibold text-gray-700 text-sm border-b pb-2">Datos Generales</h5>
                      
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Peso (kg)</label>
                        <input
                          type="number"
                          value={formData.examenFisico.peso}
                          onChange={(e) => setFormData({
                            ...formData,
                            examenFisico: { ...formData.examenFisico, peso: e.target.value }
                          })}
                          placeholder="70"
                          step="0.1"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm 
                            focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] bg-white"
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Talla (cm)</label>
                        <input
                          type="number"
                          value={formData.examenFisico.talla}
                          onChange={(e) => setFormData({
                            ...formData,
                            examenFisico: { ...formData.examenFisico, talla: e.target.value }
                          })}
                          placeholder="170"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm 
                            focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] bg-white"
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-gray-600 mb-1">IMC (Auto)</label>
                        <div className={`px-3 py-2 rounded-lg text-sm font-medium ${
                          formData.examenFisico.imc
                            ? parseFloat(formData.examenFisico.imc) < 18.5 ? 'bg-yellow-100 text-yellow-800'
                            : parseFloat(formData.examenFisico.imc) < 25 ? 'bg-green-100 text-green-800'
                            : parseFloat(formData.examenFisico.imc) < 30 ? 'bg-orange-100 text-orange-800'
                            : 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {formData.examenFisico.imc || 'Se calcula automáticamente'}
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Presión Arterial</label>
                        <input
                          type="text"
                          value={formData.examenFisico.presionArterial}
                          onChange={(e) => setFormData({
                            ...formData,
                            examenFisico: { ...formData.examenFisico, presionArterial: e.target.value }
                          })}
                          placeholder="120/80 mmHg"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm 
                            focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] bg-white"
                        />
                      </div>
                    </div>

                    {/* Columna 2: Vascular */}
                    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                      <h5 className="font-semibold text-gray-700 text-sm border-b pb-2">Evaluación Vascular</h5>
                      
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Pulso Radial</label>
                        <input
                          type="text"
                          value={formData.examenFisico.pulsoRadial}
                          onChange={(e) => setFormData({
                            ...formData,
                            examenFisico: { ...formData.examenFisico, pulsoRadial: e.target.value }
                          })}
                          placeholder="Regular, fuerte"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm 
                            focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] bg-white"
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-gray-600 mb-2">Pulsos Pedios</label>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-[10px] text-gray-500 mb-1">Izquierdo</label>
                            <select
                              value={formData.examenFisico.pulsoPedioIzq}
                              onChange={(e) => setFormData({
                                ...formData,
                                examenFisico: { ...formData.examenFisico, pulsoPedioIzq: e.target.value }
                              })}
                              className="w-full px-2 py-2 border border-gray-200 rounded-lg text-xs 
                                focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] bg-white"
                            >
                              <option value="presente">✓ Presente</option>
                              <option value="disminuido">⚠ Disminuido</option>
                              <option value="ausente">✗ Ausente</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-[10px] text-gray-500 mb-1">Derecho</label>
                            <select
                              value={formData.examenFisico.pulsoPedioDer}
                              onChange={(e) => setFormData({
                                ...formData,
                                examenFisico: { ...formData.examenFisico, pulsoPedioDer: e.target.value }
                              })}
                              className="w-full px-2 py-2 border border-gray-200 rounded-lg text-xs 
                                focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] bg-white"
                            >
                              <option value="presente">✓ Presente</option>
                              <option value="disminuido">⚠ Disminuido</option>
                              <option value="ausente">✗ Ausente</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Columna 3: Neurológico/Ortopédico */}
                    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                      <h5 className="font-semibold text-gray-700 text-sm border-b pb-2">Evaluación Neurológica</h5>
                      
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Sensibilidad (Monofilamento)</label>
                        <select
                          value={formData.examenFisico.sensibilidad}
                          onChange={(e) => setFormData({
                            ...formData,
                            examenFisico: { ...formData.examenFisico, sensibilidad: e.target.value }
                          })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm 
                            focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] bg-white"
                        >
                          <option value="conservada">✓ Conservada</option>
                          <option value="alterada">⚠ Alterada</option>
                          <option value="ausente">✗ Ausente</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Tipo de Pie</label>
                        <select
                          value={formData.examenFisico.tipoPie}
                          onChange={(e) => setFormData({
                            ...formData,
                            examenFisico: { ...formData.examenFisico, tipoPie: e.target.value }
                          })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm 
                            focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] bg-white"
                        >
                          <option value="normal">Normal</option>
                          <option value="plano">Plano</option>
                          <option value="cavo">Cavo</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Calzado</label>
                        <select
                          value={formData.examenFisico.calzado}
                          onChange={(e) => setFormData({
                            ...formData,
                            examenFisico: { ...formData.examenFisico, calzado: e.target.value }
                          })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm 
                            focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] bg-white"
                        >
                          <option value="adecuado">✓ Adecuado</option>
                          <option value="inadecuado">✗ Inadecuado</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB COSMETOLOGÍA: Datos Cosmetológicos */}
              {esCosmetologia && activeTab === (esMulti ? 2 : 0) && (
                <div className="space-y-6 animate-in fade-in duration-200">
                  <h4 className="text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-4">
                    Datos Cosmetológicos
                  </h4>

                  {/* Grid de 2 columnas */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Columna Izquierda: Tipo de piel y Fototipo */}
                    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                      <h5 className="font-semibold text-gray-700 text-sm border-b pb-2">Piel del Paciente</h5>

                      {/* Tipo de Piel */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Tipo de Piel</label>
                        <select
                          value={formData.tipo_piel}
                          onChange={(e) => setFormData({ ...formData, tipo_piel: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm 
                            focus:ring-2 focus:ring-[#E76F51] focus:border-[#E76F51] bg-white"
                        >
                          <option value="">Seleccionar tipo</option>
                          <option value="normal">Normal</option>
                          <option value="seca">Seca</option>
                          <option value="grasa">Grasa</option>
                          <option value="mixta">Mixta</option>
                          <option value="sensible">Sensible</option>
                        </select>
                      </div>

                      {/* Fototipo Fitzpatrick */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Fototipo Fitzpatrick</label>
                        <select
                          value={formData.fototipo}
                          onChange={(e) => setFormData({ ...formData, fototipo: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm 
                            focus:ring-2 focus:ring-[#E76F51] focus:border-[#E76F51] bg-white"
                        >
                          <option value="">Seleccionar fototipo</option>
                          <option value="I">I - Muy clara</option>
                          <option value="II">II - Clara</option>
                          <option value="III">III - Media</option>
                          <option value="IV">IV - Morena</option>
                          <option value="V">V - Oscura</option>
                          <option value="VI">VI - Muy oscura</option>
                        </select>
                      </div>
                    </div>

                    {/* Columna Derecha: Zona y Consentimiento */}
                    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                      <h5 className="font-semibold text-gray-700 text-sm border-b pb-2">Zona y Seguridad</h5>

                      {/* Zona Tratada */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Zona Tratada</label>
                        <input
                          type="text"
                          value={formData.zona_tratada}
                          onChange={(e) => setFormData({ ...formData, zona_tratada: e.target.value })}
                          placeholder="Rostro, cuello, abdomen..."
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm 
                            focus:ring-2 focus:ring-[#E76F51] focus:border-[#E76F51] bg-white"
                        />
                      </div>

                      {/* Consentimiento Informado */}
                      <div className={`p-3 rounded-lg border-2 transition-colors ${
                        formData.consentimiento_firmado ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-white'
                      }`}>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.consentimiento_firmado}
                            onChange={(e) => setFormData({ ...formData, consentimiento_firmado: e.target.checked })}
                            className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <span className="font-semibold text-gray-900 text-sm">
                            Consentimiento Informado Firmado ✓
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Alergias Cosméticas */}
                  <div>
                    <label className="block text-xs font-bold text-[#E76F51] uppercase tracking-wide mb-2">
                      Alergias Cosméticas
                    </label>
                    <textarea
                      value={formData.alergias_cosmeticas}
                      onChange={(e) => setFormData({ ...formData, alergias_cosmeticas: e.target.value })}
                      rows="2"
                      placeholder="Alergias a cosméticos, productos dérmicos o ingredientes..."
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                        focus:ring-2 focus:ring-[#E76F51] focus:border-[#E76F51] text-base resize-none"
                    />
                  </div>

                  {/* Productos Aplicados */}
                  <div>
                    <label className="block text-xs font-bold text-[#E76F51] uppercase tracking-wide mb-2">
                      Productos Aplicados en la Sesión
                    </label>
                    <textarea
                      value={formData.productos_aplicados}
                      onChange={(e) => setFormData({ ...formData, productos_aplicados: e.target.value })}
                      rows="3"
                      placeholder="Serums, mascarillas, cremas, peelings aplicados..."
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                        focus:ring-2 focus:ring-[#E76F51] focus:border-[#E76F51] text-base resize-none"
                    />
                  </div>
                </div>
              )}

              {/* TAB: MANICURÍA (simplificada — solo fotos + notas + insumos + próx. sesión) */}
              {esManicuria && (
                /* Pure manicuría: always show (no tabs). Multi: show when manicuría tab (id 3) is active */
                (!esMulti || activeTab === 3) && (
                <div className="animate-in fade-in duration-200">
                  <div className="space-y-6">
                    {/* 🧴 Productos usados */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-xs font-bold text-[#C2185B] uppercase tracking-wide mb-3 flex items-center gap-2">
                        🧴 Productos Usados
                      </h4>
                      {canAccessInventory && (
                        <>
                          <div className="flex flex-col sm:flex-row gap-2 mb-3">
                            <select
                              value={productoSeleccionado?.id || ''}
                              onChange={(e) => {
                                const productoId = e.target.value;
                                const producto = productosInventario.find(p => p.id.toString() === productoId);
                                setProductoSeleccionado(producto || null);
                              }}
                              className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg 
                                focus:ring-2 focus:ring-[#C2185B] focus:border-[#C2185B] text-sm"
                            >
                              <option value="">Seleccionar producto</option>
                              {productosInventario.map(producto => (
                                <option 
                                  key={producto.id} 
                                  value={producto.id}
                                  disabled={producto.stock_actual <= 0}
                                >
                                  {producto.nombre} ({producto.stock_actual} {producto.unidad_medida})
                                </option>
                              ))}
                            </select>
                            <input
                              type="number"
                              min="1"
                              max={productoSeleccionado?.stock_actual || 1}
                              value={cantidadProducto}
                              onChange={(e) => setCantidadProducto(parseInt(e.target.value))}
                              placeholder="Cant"
                              className="w-20 px-3 py-2 bg-white border border-gray-200 rounded-lg 
                                focus:ring-2 focus:ring-[#C2185B] focus:border-[#C2185B] text-sm text-center"
                            />
                            <button
                              type="button"
                              onClick={agregarProducto}
                              disabled={!productoSeleccionado}
                              className="px-3 py-2 border-2 border-[#C2185B] text-[#C2185B] rounded-lg 
                                hover:bg-[#C2185B]/5 disabled:border-gray-300 disabled:text-gray-300 
                                disabled:cursor-not-allowed transition-colors font-medium text-sm"
                            >
                              + Añadir
                            </button>
                          </div>
                          {formData.productos_usados.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {formData.productos_usados.map((producto, index) => (
                                <div 
                                  key={index} 
                                  className="inline-flex items-center gap-2 px-2 py-1 bg-white border border-gray-200 rounded-lg text-xs"
                                >
                                  <span className="font-medium text-gray-700">{producto.nombre}</span>
                                  <span className="text-[#C2185B] font-semibold">×{producto.cantidad}</span>
                                  <button
                                    type="button"
                                    onClick={() => eliminarProducto(index)}
                                    className="p-0.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                  >
                                    <XMarkIcon className="w-3 h-3" strokeWidth={2} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                      {!canAccessInventory && (
                        <p className="text-sm text-gray-400 italic">Acceso a inventario no disponible en tu plan actual.</p>
                      )}
                    </div>

                    {/* 📝 Notas de la sesión */}
                    <div>
                      <label className="block text-xs font-bold text-[#C2185B] uppercase tracking-wide mb-2">
                        📝 Notas de la sesión
                      </label>
                      <textarea
                        value={formData.descripcion_atencion}
                        onChange={(e) => setFormData({ ...formData, descripcion_atencion: e.target.value })}
                        rows="3"
                        placeholder="Color elegido, forma, diseño, observaciones de las uñas..."
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                          focus:ring-2 focus:ring-[#C2185B] focus:border-[#C2185B] text-base resize-none"
                      />
                    </div>

                    {/* 📅 Próxima sesión estimada */}
                    <div>
                      <label className="block text-xs font-bold text-[#C2185B] uppercase tracking-wide mb-2">
                        📅 Próxima sesión estimada
                      </label>
                      <input
                        type="date"
                        value={formData.proxima_sesion_estimada || ''}
                        onChange={(e) => setFormData({ ...formData, proxima_sesion_estimada: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full sm:w-auto px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg 
                          focus:ring-2 focus:ring-[#C2185B] focus:border-[#C2185B] text-base transition-all"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Fecha estimada para la próxima visita de mantenimiento
                      </p>
                    </div>

                    {/* Nota sobre fotos */}
                    <div className="pt-4 border-t border-gray-200">
                      <h5 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-2">
                        📸 Fotos Antes/Después
                      </h5>
                      <p className="text-xs text-gray-500 italic">
                        Las fotos de Antes y Después se gestionan desde la vista detallada de la ficha una vez guardada.
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {/* TAB: EVOLUCIÓN & ATENCIÓN (común) */}
              {activeTab === (esMulti ? 4 : esPodologia ? 2 : 1) && (
                <div className="animate-in fade-in duration-200">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Lado Izquierdo: Podograma (solo podología) */}
                    {esPodologia && (
                    <div>
                      <InteractivePodogram
                        markers={formData.podograma}
                        onAddMarker={(marker) => setFormData({
                          ...formData,
                          podograma: [...formData.podograma, marker]
                        })}
                        onRemoveMarker={(index) => setFormData({
                          ...formData,
                          podograma: formData.podograma.filter((_, i) => i !== index)
                        })}
                      />
                    </div>
                    )}

                    {/* Lado Derecho: Datos de Evolución */}
                    <div className={`space-y-4 ${!esPodologia ? 'lg:col-span-2' : ''}`}>
                      {/* Motivo de Consulta */}
                      <div>
                        <label className="block text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-2">
                          Motivo de Consulta
                        </label>
                        <textarea
                          value={formData.descripcion_atencion}
                          onChange={(e) => setFormData({ ...formData, descripcion_atencion: e.target.value })}
                          rows="3"
                          placeholder="¿Por qué vino el paciente?"
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                            focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] text-base resize-none"
                          required
                        />
                      </div>

                      {/* Procedimiento */}
                      <div>
                        <label className="block text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-2">
                          Procedimiento Realizado
                        </label>
                        <textarea
                          value={formData.procedimiento}
                          onChange={(e) => setFormData({ ...formData, procedimiento: e.target.value })}
                          rows="4"
                          placeholder="Describe el tratamiento realizado..."
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                            focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] text-base resize-none"
                          required
                        />
                      </div>

                      {/* Indicaciones */}
                      <div>
                        <label className="block text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-2">
                          Indicaciones al Paciente
                        </label>
                        <textarea
                          value={formData.indicaciones}
                          onChange={(e) => setFormData({ ...formData, indicaciones: e.target.value })}
                          rows="2"
                          placeholder="Recomendaciones y cuidados..."
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                            focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] text-base resize-none"
                          required
                        />
                      </div>

                      {/* Insumos Utilizados - Solo para planes con acceso a inventario */}
                      {canAccessInventory && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-3">
                          Insumos Utilizados
                        </h4>
                        
                        <div className="flex flex-col sm:flex-row gap-2 mb-3">
                          <select
                            value={productoSeleccionado?.id || ''}
                            onChange={(e) => {
                              const productoId = e.target.value;
                              const producto = productosInventario.find(p => p.id.toString() === productoId);
                              setProductoSeleccionado(producto || null);
                            }}
                            className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg 
                              focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] text-sm"
                          >
                            <option value="">Seleccionar insumo</option>
                            {productosInventario.map(producto => (
                              <option 
                                key={producto.id} 
                                value={producto.id}
                                disabled={producto.stock_actual <= 0}
                              >
                                {producto.nombre} ({producto.stock_actual} {producto.unidad_medida})
                              </option>
                            ))}
                          </select>
                          
                          <input
                            type="number"
                            min="1"
                            max={productoSeleccionado?.stock_actual || 1}
                            value={cantidadProducto}
                            onChange={(e) => setCantidadProducto(parseInt(e.target.value))}
                            placeholder="Cant"
                            className="w-20 px-3 py-2 bg-white border border-gray-200 rounded-lg 
                              focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] text-sm text-center"
                          />
                          
                          <button
                            type="button"
                            onClick={agregarProducto}
                            disabled={!productoSeleccionado}
                            className="px-3 py-2 border-2 border-[#2A9D8F] text-[#2A9D8F] rounded-lg 
                              hover:bg-[#2A9D8F]/5 disabled:border-gray-300 disabled:text-gray-300 
                              disabled:cursor-not-allowed transition-colors font-medium text-sm"
                          >
                            + Añadir
                          </button>
                        </div>
                        
                        {formData.productos_usados.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {formData.productos_usados.map((producto, index) => (
                              <div 
                                key={index} 
                                className="inline-flex items-center gap-2 px-2 py-1 bg-white border border-gray-200 rounded-lg text-xs"
                              >
                                <span className="font-medium text-gray-700">{producto.nombre}</span>
                                <span className="text-[#2A9D8F] font-semibold">×{producto.cantidad}</span>
                                <button
                                  type="button"
                                  onClick={() => eliminarProducto(index)}
                                  className="p-0.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                >
                                  <XMarkIcon className="w-3 h-3" strokeWidth={2} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      )}

                      {/* Nota sobre fotos */}
                      <div className="pt-4 border-t border-gray-200 mt-4">
                        <h5 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-2">
                          📸 Fotos de la atención
                        </h5>
                        <p className="text-xs text-gray-500 italic">
                          Nota: Las fotos de Antes y Después se gestionan desde la vista detallada de la ficha una vez que esta haya sido guardada.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Footer con botones - SIEMPRE VISIBLE */}
              <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-6 pt-6 border-t border-gray-100">
                <div className="text-sm text-gray-500">
                  Paso {activeTab + 1} de {esMulti ? 5 : esManicuria ? 1 : esPodologia ? 3 : 2}
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 
                      transition-colors font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 bg-[#E76F51] hover:bg-[#d65f41] text-white rounded-lg 
                      shadow-lg transition-all font-medium"
                  >
                    Guardar Ficha
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Vista de Ficha - Estilo Ficha Médica de Papel */}
      {viewMode === 'view' && selectedFicha && (
        <div 
          className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full z-50 p-2 sm:p-4"
          onClick={handleCloseModal}
        >
          <div 
            className="relative top-4 sm:top-10 mx-auto w-full max-w-full sm:max-w-3xl shadow-2xl rounded-xl bg-white overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Header con botones de acción */}
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-[#2A9D8F]/10 to-transparent border-b border-gray-100">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Ficha Clínica</h3>
                <p className="text-[#2A9D8F] text-sm">{formatDate(selectedFicha.fecha)}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    handleCloseModal();
                    handleEditClick(e, selectedFicha);
                  }}
                  className="p-2.5 text-gray-500 hover:text-[#2A9D8F] hover:bg-[#2A9D8F]/10 rounded-lg transition-colors"
                  title="Editar"
                >
                  <PencilIcon className="w-5 h-5" strokeWidth={1.5} />
                </button>
                <button
                  onClick={handleGenerarCertificado}
                  className="p-2.5 text-gray-500 hover:text-[#2A9D8F] hover:bg-[#2A9D8F]/10 rounded-lg transition-colors"
                  title="Generar Certificado"
                >
                  <PrinterIcon className="w-5 h-5" strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => setShowPrescription(true)}
                  className="p-2.5 text-gray-500 hover:text-[#E76F51] hover:bg-[#E76F51]/10 rounded-lg transition-colors"
                  title="Imprimir Indicaciones"
                >
                  <ClipboardDocumentListIcon className="w-5 h-5" strokeWidth={1.5} />
                </button>
                <button
                  onClick={handleCloseModal}
                  className="p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>
            </div>

            {/* Contenido estilo papel */}
            <div className="p-6 bg-white">
              {/* Info del Paciente */}
              <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-100">
                <div className="w-12 h-12 bg-[#2A9D8F] text-white rounded-full flex items-center justify-center font-bold">
                  {selectedPaciente.nombre.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()}
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{selectedPaciente.nombre}</h4>
                  <div className="text-sm text-gray-500 flex items-center gap-2">
                    <span>{selectedPaciente.rut}</span>
                    {selectedPaciente.fecha_nacimiento && (
                      <>
                        <span>•</span>
                        <span>{calcularEdad(selectedPaciente.fecha_nacimiento)} años</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Secciones de la ficha */}
              <div className="space-y-5">
                {/* Sección Antecedentes Mórbidos */}
                {selectedFicha.anamnesis && (
                  <div className="pt-4 border-t border-gray-100">
                    <h5 className="text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-3 flex items-center gap-2">
                      ❤️ Antecedentes Mórbidos
                    </h5>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="flex flex-wrap gap-2">
                        {selectedFicha.anamnesis.hta && (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 text-red-700 font-medium">
                            🩺 HTA {selectedFicha.anamnesis.htaControlada ? '(Controlada)' : ''}
                          </span>
                        )}
                        {selectedFicha.anamnesis.dm && (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-amber-100 text-amber-700 font-medium">
                            ⚠️ Diabetes {selectedFicha.anamnesis.dmTipo ? `Tipo ${selectedFicha.anamnesis.dmTipo}` : ''} {selectedFicha.anamnesis.dmAnios ? `(${selectedFicha.anamnesis.dmAnios} años)` : ''}
                          </span>
                        )}
                      </div>
                      {selectedFicha.anamnesis.alergias && (
                        <div>
                          <span className="text-xs font-semibold text-gray-500">Alergias:</span>
                          <p className="text-sm text-gray-700">{selectedFicha.anamnesis.alergias}</p>
                        </div>
                      )}
                      {selectedFicha.anamnesis.farmacos && (
                        <div>
                          <span className="text-xs font-semibold text-gray-500">Fármacos:</span>
                          <p className="text-sm text-gray-700">{selectedFicha.anamnesis.farmacos}</p>
                        </div>
                      )}
                      {selectedFicha.anamnesis.otros && (
                        <div>
                          <span className="text-xs font-semibold text-gray-500">Otras Patologías:</span>
                          <p className="text-sm text-gray-700">{selectedFicha.anamnesis.otros}</p>
                        </div>
                      )}
                      {!selectedFicha.anamnesis.hta && !selectedFicha.anamnesis.dm && !selectedFicha.anamnesis.alergias && !selectedFicha.anamnesis.farmacos && !selectedFicha.anamnesis.otros && (
                        <p className="text-sm text-gray-400 italic">Sin antecedentes registrados</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Sección Examen Físico */}
                {selectedFicha.examenFisico && (
                  <div className="pt-4 border-t border-gray-100">
                    <h5 className="text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-3 flex items-center gap-2">
                      📋 Examen Físico
                    </h5>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        {selectedFicha.examenFisico.peso && (
                          <div>
                            <span className="text-xs font-semibold text-gray-500 block">Peso</span>
                            <span className="text-gray-900 font-medium">{selectedFicha.examenFisico.peso} kg</span>
                          </div>
                        )}
                        {selectedFicha.examenFisico.talla && (
                          <div>
                            <span className="text-xs font-semibold text-gray-500 block">Talla</span>
                            <span className="text-gray-900 font-medium">{selectedFicha.examenFisico.talla} cm</span>
                          </div>
                        )}
                        {selectedFicha.examenFisico.imc && (
                          <div>
                            <span className="text-xs font-semibold text-gray-500 block">IMC</span>
                            <span className="text-gray-900 font-medium">{selectedFicha.examenFisico.imc}</span>
                          </div>
                        )}
                        {selectedFicha.examenFisico.presionArterial && (
                          <div>
                            <span className="text-xs font-semibold text-gray-500 block">Presión Arterial</span>
                            <span className="text-gray-900 font-medium">{selectedFicha.examenFisico.presionArterial}</span>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mt-4 pt-4 border-t border-gray-200">
                        {selectedFicha.examenFisico.pulsoRadial && (
                          <div>
                            <span className="text-xs font-semibold text-gray-500 block">Pulso Radial</span>
                            <span className="text-gray-900">{selectedFicha.examenFisico.pulsoRadial}</span>
                          </div>
                        )}
                        <div>
                          <span className="text-xs font-semibold text-gray-500 block">Pulso Pedio Izq.</span>
                          <span className={`font-medium ${selectedFicha.examenFisico.pulsoPedioIzq === 'presente' ? 'text-green-600' : 'text-red-600'}`}>
                            {selectedFicha.examenFisico.pulsoPedioIzq === 'presente' ? '✓ Presente' : '✗ Ausente'}
                          </span>
                        </div>
                        <div>
                          <span className="text-xs font-semibold text-gray-500 block">Pulso Pedio Der.</span>
                          <span className={`font-medium ${selectedFicha.examenFisico.pulsoPedioDer === 'presente' ? 'text-green-600' : 'text-red-600'}`}>
                            {selectedFicha.examenFisico.pulsoPedioDer === 'presente' ? '✓ Presente' : '✗ Ausente'}
                          </span>
                        </div>
                        <div>
                          <span className="text-xs font-semibold text-gray-500 block">Sensibilidad</span>
                          <span className="text-gray-900">{selectedFicha.examenFisico.sensibilidad === 'conservada' ? '✓ Conservada' : '⚠️ Disminuida'}</span>
                        </div>
                        <div>
                          <span className="text-xs font-semibold text-gray-500 block">Tipo de Pie</span>
                          <span className="text-gray-900 capitalize">{selectedFicha.examenFisico.tipoPie}</span>
                        </div>
                        <div>
                          <span className="text-xs font-semibold text-gray-500 block">Calzado</span>
                          <span className="text-gray-900 capitalize">{selectedFicha.examenFisico.calzado}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sección Podograma Visual */}
                {selectedFicha.podograma && selectedFicha.podograma.length > 0 && (
                  <div className="pt-4 border-t border-gray-100">
                    <h5 className="text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-3 flex items-center gap-2">
                      🦶 Mapa Podológico ({selectedFicha.podograma.length} lesiones)
                    </h5>
                    
                    {/* Visualización con imágenes de pies y marcadores */}
                    <div className="relative w-full max-w-md mx-auto bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                      {/* Vista Plantar */}
                      <div className="p-3 border-b border-gray-100 flex flex-col items-center bg-white">
                        <h6 className="text-[10px] font-bold text-gray-400 mb-2 uppercase tracking-widest">
                          Vista Plantar
                        </h6>
                        <img 
                          src="/pie1.png" 
                          alt="Vista Plantar" 
                          className="w-full max-w-[280px] h-auto object-contain opacity-95" 
                        />
                      </div>
                      
                      {/* Vista Dorsal */}
                      <div className="p-3 flex flex-col items-center bg-gray-50/50">
                        <h6 className="text-[10px] font-bold text-gray-400 mb-2 uppercase tracking-widest">
                          Vista Dorsal
                        </h6>
                        <img 
                          src="/pie2.png" 
                          alt="Vista Dorsal" 
                          className="w-full max-w-[280px] h-auto object-contain opacity-95" 
                        />
                      </div>
                      
                      {/* Marcadores de lesiones */}
                      {selectedFicha.podograma.map((lesion, index) => {
                        const lesionColors = {
                          'onicocriptosis': 'bg-red-500',
                          'heloma': 'bg-yellow-400',
                          'hiperqueratosis': 'bg-blue-400',
                          'verruga': 'bg-green-500',
                          'onicomicosis': 'bg-purple-500',
                          'fisura': 'bg-orange-500',
                          'ulcera': 'bg-rose-600'
                        };
                        const colorClass = lesionColors[lesion.tipo] || 'bg-gray-500';
                        
                        return (
                          <div
                            key={index}
                            className={`absolute w-5 h-5 rounded-full -translate-x-1/2 -translate-y-1/2 
                              ${colorClass} border-2 border-white shadow-md z-10 
                              flex items-center justify-center`}
                            style={{ left: `${lesion.x}%`, top: `${lesion.y}%` }}
                            title={lesion.tipo}
                          >
                            <span className="text-white text-[10px] font-bold">{index + 1}</span>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Leyenda de lesiones */}
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-semibold text-gray-600 mb-2">Lesiones Marcadas:</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedFicha.podograma.map((lesion, index) => {
                          const lesionLabels = {
                            'onicocriptosis': 'Onicocriptosis',
                            'heloma': 'Heloma',
                            'hiperqueratosis': 'Hiperqueratosis',
                            'verruga': 'Verruga Plantar',
                            'onicomicosis': 'Onicomicosis',
                            'fisura': 'Fisura',
                            'ulcera': 'Úlcera'
                          };
                          const lesionColors = {
                            'onicocriptosis': 'bg-red-500',
                            'heloma': 'bg-yellow-400',
                            'hiperqueratosis': 'bg-blue-400',
                            'verruga': 'bg-green-500',
                            'onicomicosis': 'bg-purple-500',
                            'fisura': 'bg-orange-500',
                            'ulcera': 'bg-rose-600'
                          };
                          const colorClass = lesionColors[lesion.tipo] || 'bg-gray-500';
                          const label = lesionLabels[lesion.tipo] || lesion.tipo;
                          
                          return (
                            <div 
                              key={index}
                              className="inline-flex items-center gap-2 px-2 py-1 bg-white border border-gray-200 rounded-lg text-xs"
                            >
                              <span className={`w-4 h-4 rounded-full ${colorClass} flex items-center justify-center text-white text-[10px] font-bold`}>
                                {index + 1}
                              </span>
                              <span className="font-medium">{label}</span>
                              {lesion.nota && <span className="text-gray-400">({lesion.nota})</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Sección Cosmetología */}
                {esCosmetologia && (selectedFicha.tipo_piel || selectedFicha.fototipo || selectedFicha.zona_tratada || selectedFicha.consentimiento_firmado || selectedFicha.alergias_cosmeticas || selectedFicha.productos_aplicados) && (
                  <div className="pt-4 border-t border-gray-100">
                    <h5 className="text-xs font-bold text-[#E76F51] uppercase tracking-wide mb-3 flex items-center gap-2">
                      💆 Datos Cosmetológicos
                    </h5>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        {selectedFicha.tipo_piel && (
                          <div>
                            <span className="text-xs font-semibold text-gray-500 block">Tipo de Piel</span>
                            <span className="text-gray-900 font-medium capitalize">{selectedFicha.tipo_piel}</span>
                          </div>
                        )}
                        {selectedFicha.fototipo && (
                          <div>
                            <span className="text-xs font-semibold text-gray-500 block">Fototipo</span>
                            <span className="text-gray-900 font-medium">
                              {selectedFicha.fototipo === 'I' ? 'I - Muy clara' :
                               selectedFicha.fototipo === 'II' ? 'II - Clara' :
                               selectedFicha.fototipo === 'III' ? 'III - Media' :
                               selectedFicha.fototipo === 'IV' ? 'IV - Morena' :
                               selectedFicha.fototipo === 'V' ? 'V - Oscura' :
                               selectedFicha.fototipo === 'VI' ? 'VI - Muy oscura' : selectedFicha.fototipo}
                            </span>
                          </div>
                        )}
                        {selectedFicha.zona_tratada && (
                          <div>
                            <span className="text-xs font-semibold text-gray-500 block">Zona Tratada</span>
                            <span className="text-gray-900 font-medium">{selectedFicha.zona_tratada}</span>
                          </div>
                        )}
                        <div className="col-span-2 md:col-span-3">
                          <span className="text-xs font-semibold text-gray-500 block">Consentimiento Informado</span>
                          <span className={`font-medium ${selectedFicha.consentimiento_firmado ? 'text-green-600' : 'text-red-500'}`}>
                            {selectedFicha.consentimiento_firmado ? '✓ Firmado' : '✗ No firmado'}
                          </span>
                        </div>
                      </div>
                      {selectedFicha.alergias_cosmeticas && (
                        <div className="pt-3 border-t border-gray-200">
                          <span className="text-xs font-semibold text-gray-500">Alergias Cosméticas:</span>
                          <p className="text-sm text-gray-700 mt-1">{selectedFicha.alergias_cosmeticas}</p>
                        </div>
                      )}
                      {selectedFicha.productos_aplicados && (
                        <div className="pt-3 border-t border-gray-200">
                          <span className="text-xs font-semibold text-gray-500">Productos Aplicados:</span>
                          <p className="text-sm text-gray-700 mt-1">{selectedFicha.productos_aplicados}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <h5 className="text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-2">
                    Motivo de Consulta
                  </h5>
                  <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {selectedFicha.descripcion_atencion}
                  </p>
                </div>

                <div>
                  <h5 className="text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-2">
                    Procedimiento Realizado
                  </h5>
                  <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {selectedFicha.procedimiento}
                  </p>
                </div>

                <div>
                  <h5 className="text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-2">
                    Indicaciones
                  </h5>
                  <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {selectedFicha.indicaciones}
                  </p>
                </div>

                {/* Próxima sesión estimada */}
                {selectedFicha.proxima_sesion_estimada && (
                <div className="pt-2">
                  <h5 className="text-xs font-bold text-[#C2185B] uppercase tracking-wide mb-2 flex items-center gap-2">
                    📅 Próxima sesión estimada
                  </h5>
                  <p className="text-gray-700">
                    {(() => {
                      try {
                        const d = new Date(selectedFicha.proxima_sesion_estimada + 'T12:00:00');
                        return d.toLocaleDateString('es-CL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                      } catch { return selectedFicha.proxima_sesion_estimada; }
                    })()}
                  </p>
                </div>
                )}

                {/* FC-003: Fotos de antes/después */}
                <div className="pt-4 border-t border-gray-100">
                  <h5 className="text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-3 flex items-center gap-2">
                    📸 Fotos de la atención (Evolución)
                  </h5>

                  {loadingFotos ? (
                    <div className="flex justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2A9D8F]"></div>
                    </div>
                  ) : (
                    <>
                      {/* Grid de fotos */}
                      {fotos.length > 0 ? (
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          {fotos.map(foto => (
                            <div key={foto.id} className="relative bg-gray-50 rounded-lg overflow-hidden border border-gray-200 group">
                              <img
                                src={foto.imagen_url}
                                alt={foto.descripcion || foto.tipo}
                                className="w-full h-36 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => setActivePhoto(foto)}
                              />
                              <div className="absolute top-1 left-1">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                                  foto.tipo === 'antes' ? 'bg-amber-400 text-amber-900' : 'bg-emerald-400 text-emerald-900'
                                }`}>
                                  {foto.tipo === 'antes' ? 'Antes' : 'Después'}
                                </span>
                              </div>
                              <button
                                onClick={() => handleDeleteFoto(foto.id)}
                                className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                                title="Eliminar foto"
                              >
                                <XMarkIcon className="w-3.5 h-3.5" strokeWidth={2} />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400 italic mb-3">Sin fotos registradas</p>
                      )}

                      {/* Upload */}
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex flex-wrap items-end gap-2">
                          <div>
                            <label className="block text-[10px] font-semibold text-gray-500 mb-1">Tipo</label>
                            <select
                              value={uploadTipo}
                              onChange={(e) => setUploadTipo(e.target.value)}
                              className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F]"
                            >
                              <option value="antes">Antes</option>
                              <option value="despues">Después</option>
                            </select>
                          </div>
                          <div className="flex-1 min-w-0">
                            <label className="block text-[10px] font-semibold text-gray-500 mb-1">Imagen</label>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleFileSelect}
                              className="block w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-[#2A9D8F]/10 file:text-[#2A9D8F] hover:file:bg-[#2A9D8F]/20 cursor-pointer"
                            />
                          </div>
                          <button
                            onClick={handleUploadFoto}
                            disabled={!uploadFile || uploading}
                            className="px-3 py-1.5 bg-[#2A9D8F] text-white rounded-lg text-sm font-medium hover:bg-[#248f82] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                          >
                            {uploading ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            ) : (
                              'Subir'
                            )}
                          </button>
                        </div>
                        {uploadPreview && (
                          <div className="mt-2">
                            <img src={uploadPreview} alt="Preview" className="h-20 w-20 object-cover rounded-lg border border-gray-200" />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Sección Tratamientos */}
                {selectedFicha.tratamientos && selectedFicha.tratamientos.length > 0 && (
                  <div className="pt-4 border-t border-gray-100">
                    <h5 className="text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-3 flex items-center gap-2">
                      🩺 Tratamientos Realizados
                    </h5>
                    <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                      {selectedFicha.tratamientos.map((trat, index) => (
                        <div key={index} className="flex justify-between text-sm">
                          <span className="text-gray-700">{trat.nombre || trat.tratamiento_nombre}</span>
                          {trat.precio && (
                            <span className="font-medium text-[#2A9D8F]">${trat.precio?.toLocaleString('es-CL')}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sección Insumos Utilizados - Mejorada */}
                {selectedFicha.productos_usados && selectedFicha.productos_usados.length > 0 && (
                  <div className="pt-4 border-t border-gray-100">
                    <h5 className="text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-3 flex items-center gap-2">
                      📦 Insumos Utilizados
                    </h5>
                    <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                      {selectedFicha.productos_usados.map((producto, index) => (
                        <div key={index} className="flex justify-between text-sm">
                          <span className="text-gray-700">{producto.nombre_insumo || producto.nombre}</span>
                          <span className="text-gray-500">x{producto.cantidad}</span>
                        </div>
                      ))}
                    </div>
                    {selectedFicha.costo_total_formato && (
                      <div className="mt-3 pt-3 border-t border-gray-100 text-right">
                        <span className="text-sm text-gray-500">Costo Total: </span>
                        <span className="text-lg font-bold text-gray-900">{selectedFicha.costo_total_formato}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Modal de Edición - Grid Bimodal */}
      {viewMode === 'edit' && (
        <div 
          className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full z-50 p-2 sm:p-4"
          onClick={handleCloseModal}
        >
          <div 
            className="relative top-4 sm:top-6 mx-auto p-5 sm:p-6 border-0 w-full max-w-full sm:max-w-5xl shadow-2xl rounded-xl bg-white"
            onClick={e => e.stopPropagation()}
          >
            {/* Header del Modal */}
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
              <h3 className="text-xl font-bold text-gray-900">
                Editar Ficha Clínica
              </h3>
              <button
                onClick={handleCloseModal}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-6 h-6" strokeWidth={1.5} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Grid de 12 columnas (5 izq / 7 der) */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                {/* Columna Izquierda - Contexto (5 columnas) */}
                <div className="md:col-span-5 space-y-4">
                  {/* Fecha */}
                  <div>
                    <label className="block text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-2">
                      Fecha de Atención
                    </label>
                    <input
                      type="date"
                      value={formData.fecha}
                      onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                        focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] 
                        text-base transition-all"
                      required
                    />
                  </div>

                  {/* Motivo de Consulta */}
                  <div>
                    <label className="block text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-2">
                      Motivo de Consulta
                    </label>
                    <textarea
                      value={formData.descripcion_atencion}
                      onChange={(e) => setFormData({ ...formData, descripcion_atencion: e.target.value })}
                      rows="4"
                      placeholder="¿Por qué vino el paciente? Evaluación inicial..."
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                        focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] 
                        text-base resize-none transition-all"
                      required
                    />
                  </div>
                </div>

                {/* Columna Derecha - Tratamiento (7 columnas) */}
                <div className="md:col-span-7 space-y-4 md:border-l md:pl-6 border-gray-100">
                  {/* Procedimiento - Textarea ALTA */}
                  <div>
                    <label className="block text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-2">
                      Procedimiento Realizado
                    </label>
                    <textarea
                      value={formData.procedimiento}
                      onChange={(e) => setFormData({ ...formData, procedimiento: e.target.value })}
                      rows="8"
                      placeholder="Describe el tratamiento realizado..."
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                        focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] 
                        text-base resize-none transition-all"
                      required
                    />
                  </div>

                  {/* Indicaciones - Textarea pequeña */}
                  <div>
                    <label className="block text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-2">
                      Indicaciones al Paciente
                    </label>
                    <textarea
                      value={formData.indicaciones}
                      onChange={(e) => setFormData({ ...formData, indicaciones: e.target.value })}
                      rows="3"
                      placeholder="Recomendaciones y cuidados..."
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                        focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] 
                        text-base resize-none transition-all"
                      required
                    />
                  </div>

                  {/* Próxima sesión estimada (manicuría y general) */}
                  {esManicuria && (
                  <div>
                    <label className="block text-xs font-bold text-[#C2185B] uppercase tracking-wide mb-2">
                      📅 Próxima sesión estimada
                    </label>
                    <input
                      type="date"
                      value={formData.proxima_sesion_estimada || ''}
                      onChange={(e) => setFormData({ ...formData, proxima_sesion_estimada: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full sm:w-auto px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                        focus:ring-2 focus:ring-[#C2185B] focus:border-[#C2185B] 
                        text-base transition-all"
                    />
                  </div>
                  )}
                </div>

                {/* Fila Cosmetología (solo si esCosmetologia) */}
                {esCosmetologia && (
                <div className="md:col-span-12 mt-2 pt-4 border-t border-gray-100">
                  <h4 className="text-xs font-bold text-[#E76F51] uppercase tracking-wide mb-4">
                    💆 Datos Cosmetológicos
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Tipo de Piel</label>
                      <select
                        value={formData.tipo_piel}
                        onChange={(e) => setFormData({ ...formData, tipo_piel: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm 
                          focus:ring-2 focus:ring-[#E76F51] focus:border-[#E76F51] bg-white"
                      >
                        <option value="">Seleccionar</option>
                        <option value="normal">Normal</option>
                        <option value="seca">Seca</option>
                        <option value="grasa">Grasa</option>
                        <option value="mixta">Mixta</option>
                        <option value="sensible">Sensible</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Fototipo Fitzpatrick</label>
                      <select
                        value={formData.fototipo}
                        onChange={(e) => setFormData({ ...formData, fototipo: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm 
                          focus:ring-2 focus:ring-[#E76F51] focus:border-[#E76F51] bg-white"
                      >
                        <option value="">Seleccionar</option>
                        <option value="I">I - Muy clara</option>
                        <option value="II">II - Clara</option>
                        <option value="III">III - Media</option>
                        <option value="IV">IV - Morena</option>
                        <option value="V">V - Oscura</option>
                        <option value="VI">VI - Muy oscura</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Zona Tratada</label>
                      <input
                        type="text"
                        value={formData.zona_tratada}
                        onChange={(e) => setFormData({ ...formData, zona_tratada: e.target.value })}
                        placeholder="Rostro, cuello, abdomen..."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm 
                          focus:ring-2 focus:ring-[#E76F51] focus:border-[#E76F51] bg-white"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Alergias Cosméticas</label>
                      <textarea
                        value={formData.alergias_cosmeticas}
                        onChange={(e) => setFormData({ ...formData, alergias_cosmeticas: e.target.value })}
                        rows="2"
                        placeholder="Alergias a cosméticos o productos dérmicos..."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm 
                          focus:ring-2 focus:ring-[#E76F51] focus:border-[#E76F51] bg-white resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Productos Aplicados</label>
                      <textarea
                        value={formData.productos_aplicados}
                        onChange={(e) => setFormData({ ...formData, productos_aplicados: e.target.value })}
                        rows="2"
                        placeholder="Serums, mascarillas, cremas..."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm 
                          focus:ring-2 focus:ring-[#E76F51] focus:border-[#E76F51] bg-white resize-none"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className={`inline-flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                      formData.consentimiento_firmado ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-white'
                    }`}>
                      <input
                        type="checkbox"
                        checked={formData.consentimiento_firmado}
                        onChange={(e) => setFormData({ ...formData, consentimiento_firmado: e.target.checked })}
                        className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="font-semibold text-gray-900 text-sm">
                        Consentimiento Informado Firmado
                      </span>
                    </label>
                  </div>
                </div>
                )}

                {/* Fila Inferior - Productos (12 columnas completas) */}
                <div className="md:col-span-12 mt-4 pt-4 border-t border-gray-100 bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-4">
                    Insumos Utilizados
                  </h4>
                
                  <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    <div className="flex-1">
                      <select
                        value={productoSeleccionado?.id || ''}
                        onChange={(e) => {
                          const productoId = e.target.value;
                          const producto = productosInventario.find(p => p.id.toString() === productoId);
                          setProductoSeleccionado(producto || null);
                        }}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg 
                          focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] text-base"
                      >
                        <option value="">Seleccione un producto</option>
                        {productosInventario.map(producto => (
                          <option 
                            key={producto.id} 
                            value={producto.id}
                            disabled={producto.stock_actual <= 0}
                          >
                            {producto.nombre} ({producto.stock_actual} {producto.unidad_medida})
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="w-full sm:w-24">
                      <input
                        type="number"
                        min="1"
                        max={productoSeleccionado?.stock_actual || 1}
                        value={cantidadProducto}
                        onChange={(e) => setCantidadProducto(parseInt(e.target.value))}
                        placeholder="Cant."
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg 
                          focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] text-base text-center"
                      />
                    </div>
                    
                    <button
                      type="button"
                      onClick={agregarProducto}
                      disabled={!productoSeleccionado}
                      className="px-4 py-3 border-2 border-[#2A9D8F] text-[#2A9D8F] rounded-lg 
                        hover:bg-[#2A9D8F]/5 disabled:border-gray-300 disabled:text-gray-300 
                        disabled:cursor-not-allowed transition-colors font-medium text-sm"
                    >
                      + Agregar
                    </button>
                  </div>
                  
                  {/* Lista de productos agregados */}
                  {formData.productos_usados.length > 0 && (
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <div className="flex flex-wrap gap-2">
                        {formData.productos_usados.map((producto, index) => (
                          <div 
                            key={index} 
                            className="inline-flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"
                          >
                            <span className="font-medium text-gray-700">{producto.nombre}</span>
                            <span className="text-[#2A9D8F] font-semibold">×{producto.cantidad}</span>
                            <button
                              type="button"
                              onClick={() => eliminarProducto(index)}
                              className="ml-1 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                            >
                              <XMarkIcon className="w-4 h-4" strokeWidth={2} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer con botones */}
              <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6 pt-6 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 
                    transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-[#E76F51] hover:bg-[#d65f41] text-white rounded-lg 
                    shadow-lg transition-all font-medium"
                >
                  Guardar Cambios
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Certificado Médico - Split View */}
      {showCertificado && (
        <div 
          className="fixed inset-0 bg-black/50 overflow-hidden h-full w-full z-50"
          onClick={() => setShowCertificado(false)}
        >
          <div 
            className="absolute inset-4 sm:inset-8 mx-auto max-w-6xl bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white print:hidden">
              <h3 className="text-xl font-bold text-gray-900">Generar Certificado</h3>
              <button
                onClick={() => setShowCertificado(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-6 h-6" strokeWidth={1.5} />
              </button>
            </div>

            {/* Split View Container */}
            <div className="flex-1 flex flex-col md:flex-row overflow-hidden print:block">
              {/* Panel Izquierdo - Controles (40%) */}
              <div className="w-full md:w-2/5 p-6 overflow-y-auto bg-white border-r border-gray-100 print:hidden">
                <h4 className="text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-4">
                  Configuración del Certificado
                </h4>
                
                <div className="space-y-5">
                  <div>
                    <label className="block text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-2">
                      Tipo de Certificado
                    </label>
                    <select
                      value={certificadoData.tipo_certificado}
                      onChange={(e) => setCertificadoData({ ...certificadoData, tipo_certificado: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                        focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] text-base"
                    >
                      <option value="general">Certificado General</option>
                      <option value="deportivo">Certificado Deportivo</option>
                      <option value="laboral">Certificado Laboral</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-2">
                      Motivo / Diagnóstico
                    </label>
                    <textarea
                      value={certificadoData.motivo}
                      onChange={(e) => setCertificadoData({ ...certificadoData, motivo: e.target.value })}
                      rows="3"
                      placeholder="Motivo de la atención..."
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                        focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] text-base resize-y"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-2">
                      Indicación
                    </label>
                    <textarea
                      value={certificadoData.indicacion}
                      onChange={(e) => setCertificadoData({ ...certificadoData, indicacion: e.target.value })}
                      rows="3"
                      placeholder="Indicaciones del tratamiento..."
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                        focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] text-base resize-y"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-2">
                      Días de Reposo
                    </label>
                    <input
                      type="number"
                      value={certificadoData.dias_reposo}
                      onChange={(e) => setCertificadoData({ ...certificadoData, dias_reposo: e.target.value })}
                      placeholder="0"
                      min="0"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                        focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] text-base"
                    />
                  </div>
                </div>
              </div>

              {/* Panel Derecho - Previsualización (60%) */}
              <div className="w-full md:w-3/5 bg-gray-100 p-6 overflow-y-auto print:bg-white print:p-0">
                <div className="md:hidden text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-4 print:hidden">
                  Previsualización
                </div>

                {/* Alerta de Perfil Incompleto */}
                {(!currentUser?.rut_profesional || !currentUser?.rol_minsal || !currentUser?.direccion_consulta) && (
                  <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-md flex items-start gap-3 print:hidden">
                    <svg className="h-6 w-6 text-yellow-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <h4 className="text-sm font-bold text-yellow-800">
                        Faltan datos en tu perfil profesional
                      </h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        El certificado se generará sin tu RUT, ROL o Dirección. 
                        <a 
                          href="/app/equipo"
                          className="underline font-semibold ml-1 hover:text-yellow-900"
                        >
                          Configurar ahora
                        </a>.
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Hoja de papel */}
                <div className="bg-white shadow-xl rounded-lg p-8 max-w-[21cm] mx-auto print:shadow-none print:p-0 print:max-w-none certificado-contenedor">
                  <div className="text-center mb-8">
                    <h1 className="text-2xl font-bold mb-2">CERTIFICADO PODOLÓGICO</h1>
                    <p className="text-gray-600">{
                      certificadoData.tipo_certificado === 'deportivo' ? '(APTITUD DEPORTIVA)' :
                      certificadoData.tipo_certificado === 'laboral' ? '(APTITUD LABORAL)' : ''
                    }</p>
                  </div>

                  <div className="mb-8 space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      La profesional que suscribe certifica que:
                    </p>
                    <p>
                      Don/Doña <strong>{selectedPaciente.nombre}</strong>, 
                      RUT <strong>{selectedPaciente.rut}</strong>, 
                      ha sido atendido/a en esta consulta podológica con fecha <strong>{formatDate(selectedFicha.fecha)}</strong>.
                    </p>
                    {certificadoData.motivo && (
                      <p>
                        <strong>Diagnóstico / Motivo de atención:</strong><br />
                        {certificadoData.motivo}
                      </p>
                    )}
                    {certificadoData.indicacion && (
                      <p>
                        <strong>Indicación:</strong><br />
                        {certificadoData.indicacion}
                      </p>
                    )}
                    {certificadoData.dias_reposo && (
                      <p>
                        Se indica reposo por <strong>{certificadoData.dias_reposo} días</strong>.
                      </p>
                    )}
                    <p>
                      El presente certificado se extiende a solicitud del interesado y para los fines que estime convenientes.
                    </p>
                  </div>

                  <div className="text-center mb-8">
                    <p>{currentUser?.cuenta?.ciudad || currentUser?.ciudad_consulta || ''}, {format(new Date(), "d 'de' MMMM 'de' yyyy", { locale: es })}</p>
                  </div>

                  <div className="mt-16 pt-8 border-t border-gray-300">
                    <div className="text-center">
                      <div className="mb-4 h-20">
                        {/* Espacio para firma */}
                      </div>
                      <p className="font-medium">{currentUser?.first_name} {currentUser?.last_name}</p>
                      <p>{currentUser?.titulo_profesional || 'Podólogo/a Clínico/a'}</p>
                      <p>RUT: {currentUser?.rut_profesional || 'No registrado'}</p>
                      <p>ROL Minsal: N° {currentUser?.rol_minsal || 'Pendiente'}</p>
                      <p className="text-sm text-gray-500 mt-2">{currentUser?.direccion_consulta || 'Dirección no registrada'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Fijo */}
            <div className="px-6 py-4 bg-white border-t border-gray-100 flex justify-end gap-3 print:hidden">
              <button
                onClick={() => setShowCertificado(false)}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handlePrintCertificado}
                className="px-6 py-3 bg-[#E76F51] hover:bg-[#d65f41] text-white rounded-lg shadow-lg transition-all font-medium flex items-center gap-2"
              >
                <PrinterIcon className="w-5 h-5" strokeWidth={1.5} />
                Imprimir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Indicaciones/Receta */}
      <PrescriptionModal
        isOpen={showPrescription}
        onClose={() => setShowPrescription(false)}
        paciente={selectedPaciente}
        ficha={selectedFicha}
        currentUser={currentUser}
        formatDate={formatDate}
      />

      {/* Lightbox / Modal para ampliar fotos */}
      {activePhoto && (
        <div 
          className="fixed inset-0 bg-black/85 flex flex-col items-center justify-center z-[100] p-4 animate-in fade-in duration-200 cursor-zoom-out"
          onMouseDown={handlePhotoBackdropMouseDown}
          onMouseUp={handlePhotoBackdropMouseUp}
        >
          {/* Contenedor relativo de imagen y flechas */}
          <div className="relative max-w-4xl max-h-[85vh] w-full flex flex-col items-center justify-center cursor-default" onMouseDown={e => e.stopPropagation()} onMouseUp={e => e.stopPropagation()}>
            {/* Botón Cerrar */}
            <button
              onClick={() => setActivePhoto(null)}
              className="absolute -top-12 right-0 p-2 text-white hover:text-gray-300 bg-black/30 hover:bg-black/50 rounded-full transition-all"
              title="Cerrar"
            >
              <XMarkIcon className="w-6 h-6" strokeWidth={2} />
            </button>

            {/* Flecha Izquierda (Anterior) */}
            {fotos.length > 1 && (
              <button
                onClick={handlePrevPhoto}
                className="absolute left-2 sm:left-4 p-3 rounded-full bg-black/50 hover:bg-[#2A9D8F] text-white transition-all z-10 shadow-lg hover:scale-105"
                title="Anterior (←)"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {/* Flecha Derecha (Siguiente) */}
            {fotos.length > 1 && (
              <button
                onClick={handleNextPhoto}
                className="absolute right-2 sm:right-4 p-3 rounded-full bg-black/50 hover:bg-[#2A9D8F] text-white transition-all z-10 shadow-lg hover:scale-105"
                title="Siguiente (→)"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            <img
              src={activePhoto.imagen_url}
              alt={activePhoto.descripcion || activePhoto.tipo}
              className="max-w-[90%] max-h-[75vh] object-contain rounded-lg shadow-2xl border border-white/10"
            />

            <div className="mt-3 flex items-center gap-3">
              {activePhoto.tipo && (
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${
                  activePhoto.tipo === 'antes' ? 'bg-amber-400 text-amber-900' : 'bg-emerald-400 text-emerald-900'
                }`}>
                  {activePhoto.tipo === 'antes' ? 'Antes' : 'Después'}
                </span>
              )}
              {fotos.length > 1 && (
                <span className="text-white/80 bg-black/40 px-2.5 py-0.5 rounded-full text-xs font-medium">
                  {fotos.findIndex(f => f.id === activePhoto.id) + 1} de {fotos.length}
                </span>
              )}
            </div>
            
            {activePhoto.descripcion && (
              <p className="mt-2 text-sm text-gray-200 text-center max-w-md bg-black/20 px-3 py-1 rounded">
                {activePhoto.descripcion}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FichaClinicaPage; 
